import React from 'react';
import { Box, Typography } from '@mui/material';
import { useParams, useOutletContext } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { SectionCard, SectionHeader, SectionLoading, LegendItem } from '../components/ui/Section';
import { useChartStyles, ChartTooltipCard, ChartLoadingOverlay } from '../components/ui/chartStyles';
import { formatCompact } from '../components/ui/charts';
import { distinctColor } from '../utils/colors';
import { useInfluenceIndexData } from '../hooks/useInfluenceIndexData';
import InfluenceRanking from '../components/InfluenceRanking';
import TopPosts from '../components/TopPosts';
import EngagementByPillar from '../components/EngagementByPillar';
import BestTimeToPost from '../components/BestTimeToPost';
import { useTopPostsData } from '../hooks/useTopPostsData';

// Interface for the context provided by the Outlet (from MainLayout)
interface FilterProps {
  selectedBrands: string[];
  selectedPlatforms: string[]; // Keep for consistency, though not used here
  selectedPillars: string[];   // Keep for consistency, though not used here
  startDate: string | null; // Comes as formatted string from MainLayout
  endDate: string | null;   // Comes as formatted string from MainLayout
}

const EmployeeBrandGeneralMetrics: React.FC = () => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  const { 
    startDate, 
    endDate, 
    selectedBrands, 
    selectedPillars
  } = useOutletContext<FilterProps>();

  const cs = useChartStyles();
  const { data, loading: isLoading, error } = useInfluenceIndexData(
    orgId,
    startDate,
    endDate,
    selectedPillars,
    true // personal branding flag
  );

  const { 
    data: topPostsData, 
    loading: topPostsLoading, 
    error: topPostsError 
  } = useTopPostsData(
    orgId,
    selectedBrands,
    selectedPillars,
    ['linkedin'], // Keep LinkedIn platform for General Metrics context
    startDate,
    endDate
  );

  const chartData = React.useMemo(() => {
    if (!data?.blove_chart) return [];
    
    const { dates, brands, values, displayDates } = data.blove_chart;
    if (!dates || !brands || !values || !displayDates || dates.length === 0) return [];

    const filteredChartData = [];
    for (let i = 0; i < dates.length; i++) {
      const currentDate = dates[i]; // Date string from API (YYYY-MM-DD)
      
      // Check if currentDate is within the selected range (inclusive)
      // Direct string comparison works for YYYY-MM-DD format
      const isAfterStart = startDate ? currentDate >= startDate : true;
      const isBeforeEnd = endDate ? currentDate <= endDate : true;

      if (isAfterStart && isBeforeEnd) {
        const dataPoint: { date: string; displayDate: string; [key: string]: number | string | null } = {
          date: currentDate,
          displayDate: displayDates[i] || currentDate,
        };
        brands.forEach((brand, brandIndex) => {
          dataPoint[brand] = values[brandIndex]?.[i] ?? null;
        });
        filteredChartData.push(dataPoint);
      }
    }
    return filteredChartData;
  }, [data, startDate, endDate]);

  const peopleToDisplay = React.useMemo(() => {
    const allPeople = data?.blove_chart?.brands;
    if (!allPeople) {
      return [];
    }

    if (selectedBrands && selectedBrands.length > 0) {
      return allPeople.filter(person => selectedBrands.includes(person));
    }
    return []; 
  }, [data, selectedBrands]);

  const allPeople = data?.blove_chart?.brands ?? [];
  // Color each person by their position in the full list so they keep the
  // same color when the People filter changes (and match Influence Ranking).
  const personColor = (name: string) => distinctColor(Math.max(0, allPeople.indexOf(name)), allPeople.length);

  const indexHeader = (
    <SectionHeader
      title="Influence Index"
      info="A rolling score of each person's audience influence on LinkedIn, built from the engagement their posts earn. Higher means their content resonates more."
    />
  );

  let indexSection: React.ReactNode;
  if (isLoading && !data) {
    indexSection = (
      <SectionCard>
        {indexHeader}
        <SectionLoading height={360} />
      </SectionCard>
    );
  } else if (error) {
    indexSection = (
      <SectionCard>
        {indexHeader}
        <Typography color="error" sx={{ py: 5, textAlign: 'center' }}>Influence Index didn't load: {error}</Typography>
      </SectionCard>
    );
  } else if (!data || !data.blove_chart || chartData.length === 0) {
    indexSection = (
      <SectionCard>
        {indexHeader}
        <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
          No Influence Index data for these dates and people. Try a wider date range.
        </Typography>
      </SectionCard>
    );
  } else {
    indexSection = (
      <SectionCard>
        {indexHeader}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mb: 2.5 }}>
          {peopleToDisplay.map((personName) => (
            <LegendItem key={personName} color={personColor(personName)} label={personName} />
          ))}
        </Box>
        <Box sx={{ height: 380, width: '100%', position: 'relative' }}>
          {isLoading && data && <ChartLoadingOverlay />}
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid {...cs.grid} />
              <XAxis
                {...cs.xAxis}
                dataKey="displayDate"
                interval="preserveStartEnd"
                minTickGap={36}
              />
              <YAxis {...cs.yAxis} width={56} tickFormatter={(v) => formatCompact(v)} label={cs.axisLabel('Influence score')} />
              <Tooltip
                cursor={cs.lineCursor}
                content={({ active, payload, label }: any) =>
                  active && payload && payload.length ? (
                    <ChartTooltipCard
                      title={label}
                      rows={[...payload]
                        .filter((e: any) => e.value != null)
                        .sort((x: any, y: any) => y.value - x.value)
                        .map((e: any) => ({ key: e.name, color: e.color, label: e.name, value: Math.round(e.value).toLocaleString() }))}
                    />
                  ) : null
                }
              />
              {peopleToDisplay.map((personName) => (
                <Line
                  key={personName}
                  type="monotone"
                  dataKey={personName}
                  stroke={personColor(personName)}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3.5, strokeWidth: 0 }}
                  isAnimationActive={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </SectionCard>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {indexSection}

      {data && (
        <InfluenceRanking
          data={data}
          selectedBrands={selectedBrands}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      {topPostsError ? (
        <Typography color="error">Top posts didn't load: {topPostsError}</Typography>
      ) : (
        <TopPosts
          posts={topPostsData?.posts ?? null}
          total={topPostsData?.total ?? null}
          loading={topPostsLoading && !topPostsData}
        />
      )}

      {/* Engagement By Pillar */}
      <EngagementByPillar
        selectedPillars={selectedPillars}
        selectedBrands={selectedBrands}
        startDate={startDate}
        endDate={endDate}
      />

      {/* Best Time To Post */}
      <BestTimeToPost
        selectedPillars={selectedPillars}
        selectedBrands={selectedBrands}
        startDate={startDate}
        endDate={endDate}
      />
    </Box>
  );
};

export default EmployeeBrandGeneralMetrics; 